import { z } from 'zod';
import { issuedInvitationSchema } from './invitations.js';
import { CUSTOMER_NUMBER_PATTERN } from './catalog-feed.js';

export const TENANT_KINDS = ['MASTER', 'CUSTOMER'] as const;
export const tenantKindSchema = z.enum(TENANT_KINDS);
export type TenantKind = z.infer<typeof tenantKindSchema>;

export const TENANT_STATUSES = [
  'PROVISIONING',
  'ACTIVE',
  'SUSPENDED',
  'CANCELLED',
  'CLOSED',
] as const;
export const tenantStatusSchema = z.enum(TENANT_STATUSES);
export type TenantStatus = z.infer<typeof tenantStatusSchema>;

export const DOMAIN_KINDS = ['SYSTEM', 'CUSTOM'] as const;
export const domainKindSchema = z.enum(DOMAIN_KINDS);
export type DomainKind = z.infer<typeof domainKindSchema>;

/**
 * - PENDING: added; the worker checks DNS until the address points at the platform and the store
 *   has proven it is its own
 * - VERIFYING: DNS is right and ownership is proven; the edge may now obtain the certificate, and
 *   the worker waits for HTTPS to answer
 * - ACTIVE: serves the store
 * - FAILED: the checks gave up (`reason` says what was missing); the owner can start them again
 * - DNS_LOST: was ACTIVE, and DNS stopped pointing at the platform; the store is served on its
 *   other addresses until it comes back
 * - DISABLED: switched off by the platform
 */
export const DOMAIN_STATUSES = [
  'PENDING',
  'VERIFYING',
  'ACTIVE',
  'FAILED',
  'DISABLED',
  'DNS_LOST',
] as const;
export const domainStatusSchema = z.enum(DOMAIN_STATUSES);
export type DomainStatus = z.infer<typeof domainStatusSchema>;

/**
 * What a domain is still missing, or what it lost.
 * - NO_RECORD: the hostname has no address in DNS
 * - WRONG_TARGET: it points somewhere else
 * - OWNERSHIP_NOT_PROVEN: it points at the platform, but neither the CNAME to this store's
 *   address nor the TXT record with this store's token is there
 * - CAA_BLOCKED: the domain's CAA records forbid our certificate authority
 * - TLS_ERROR: DNS is right, but HTTPS does not answer with a valid certificate yet
 */
export const DOMAIN_REASONS = [
  'NO_RECORD',
  'WRONG_TARGET',
  'OWNERSHIP_NOT_PROVEN',
  'CAA_BLOCKED',
  'TLS_ERROR',
] as const;
export const domainReasonSchema = z.enum(DOMAIN_REASONS);
export type DomainReason = z.infer<typeof domainReasonSchema>;

/** Lifecycle actions exposed to the platform, with the only status each may start from. */
export const TENANT_LIFECYCLE_TRANSITIONS = {
  suspend: { from: 'ACTIVE', to: 'SUSPENDED' },
  reactivate: { from: 'SUSPENDED', to: 'ACTIVE' },
} as const satisfies Record<string, { from: TenantStatus; to: TenantStatus }>;
export type TenantLifecycleAction = keyof typeof TENANT_LIFECYCLE_TRANSITIONS;

/**
 * Sub-domain labels a tenant may never take: they name platform hosts, or would confuse
 * customers and mail. Compared after lower-casing.
 */
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  'www',
  'api',
  'app',
  'platform',
  'manage',
  'admin',
  'administrator',
  'stores',
  'store',
  'shop',
  'mail',
  'email',
  'smtp',
  'imap',
  'pop',
  'ftp',
  'ns',
  'ns1',
  'ns2',
  'dns',
  'mx',
  'cdn',
  'static',
  'assets',
  'media',
  'img',
  'images',
  'files',
  'docs',
  'help',
  'support',
  'status',
  'billing',
  'account',
  'accounts',
  'auth',
  'login',
  'logout',
  'signup',
  'register',
  'dashboard',
  'staging',
  'dev',
  'test',
  'demo',
  'internal',
  'system',
  'root',
  'security',
  'webmail',
  'avrahami',
]);

export const SLUG_PATTERN = /^[a-z0-9](-?[a-z0-9])*$/;

/** A tenant's URL label: `<slug>.<platform store domain>`. */
export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(40)
  .regex(SLUG_PATTERN, 'lower-case letters, digits and single hyphens only')
  .refine((slug) => !RESERVED_SLUGS.has(slug), 'this name is reserved');

/**
 * The reserved list stops tenants from claiming platform names. The platform itself may still
 * create the master tenant under one of them (for example `avrahami`).
 */
export const masterSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(40)
  .regex(SLUG_PATTERN, 'lower-case letters, digits and single hyphens only');

const HOSTNAME_PATTERN =
  /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;

/** A lower-case DNS hostname without port or trailing dot. */
export const hostnameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(HOSTNAME_PATTERN, 'invalid hostname');

/**
 * Normalizes a Host header value for lookup: lower-case, no port, no trailing dot.
 * Returns undefined for anything that is not a plain hostname, so lookups fail closed.
 */
export function normalizeHost(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  const withoutPort = value
    .trim()
    .toLowerCase()
    .replace(/:\d{1,5}$/, '');
  const hostname = withoutPort.endsWith('.') ? withoutPort.slice(0, -1) : withoutPort;
  return HOSTNAME_PATTERN.test(hostname) ? hostname : undefined;
}

/**
 * Path addressing (docs/DOMAINS.md, adr/0021): the system addresses of the stores are not served
 * as hostnames but as the first path segment of one shared host, `https://<pathHost>/<slug>`.
 * A store's system hostname in the database stays `<slug>.<systemDomain>`: it is the store's
 * identity for the API, and no DNS record or certificate exists for it.
 */
export interface PathAddressing {
  /** The one public host of every system address, e.g. `websys.example.co.il`. */
  pathHost: string;
  /** The parent of the system hostnames (`PLATFORM_STORE_DOMAIN`). */
  systemDomain: string;
}

/** Where shoppers reach a hostname of a store: the public host, and the path the store lives under. */
export interface StoreAddress {
  host: string;
  /** `/<slug>` for a system address under path addressing, `''` otherwise. */
  basePath: string;
}

/** The slug of a system hostname (`<slug>.<systemDomain>`); undefined for any other hostname. */
function systemSlug(hostname: string, systemDomain: string): string | undefined {
  const suffix = `.${systemDomain}`;
  if (!hostname.endsWith(suffix)) return undefined;
  const slug = hostname.slice(0, -suffix.length);
  return SLUG_PATTERN.test(slug) ? slug : undefined;
}

/**
 * The public address of a store hostname. A store's own domain is always its own host; a system
 * hostname is a path of the shared host when path addressing is on.
 */
export function storeAddress(hostname: string, addressing: PathAddressing | null): StoreAddress {
  const slug = addressing === null ? undefined : systemSlug(hostname, addressing.systemDomain);
  return addressing === null || slug === undefined
    ? { host: hostname, basePath: '' }
    : { host: addressing.pathHost, basePath: `/${slug}` };
}

/** The address of a store hostname as a link. */
export function storeUrl(hostname: string, addressing: PathAddressing | null): string {
  const address = storeAddress(hostname, addressing);
  return `https://${address.host}${address.basePath}`;
}

/**
 * The store a path of the shared host names: `/<slug>/p/5` is the store `<slug>.<systemDomain>`
 * at `/p/5`. Null when the first segment is not a store name (the host's own root, a file).
 */
export function storeOfPath(
  pathname: string,
  addressing: PathAddressing,
): { hostname: string; basePath: string; path: string } | null {
  const [, first = '', ...rest] = pathname.split('/');
  if (first.length < 3 || first.length > 40 || !SLUG_PATTERN.test(first)) return null;
  return {
    hostname: `${first}.${addressing.systemDomain}`,
    basePath: `/${first}`,
    path: `/${rest.join('/')}`,
  };
}

export const displayNameSchema = z.string().trim().min(1).max(120);

export const tenantSummarySchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  displayName: z.string(),
  kind: tenantKindSchema,
  status: tenantStatusSchema,
  version: z.number().int(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type TenantSummary = z.infer<typeof tenantSummarySchema>;

export const storeSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  version: z.number().int(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Store = z.infer<typeof storeSchema>;

export const domainSchema = z.object({
  id: z.uuid(),
  storeId: z.uuid(),
  hostname: z.string(),
  /** The address as a link. Not always `https://<hostname>`: see `storeUrl`. */
  url: z.string(),
  kind: domainKindSchema,
  status: domainStatusSchema,
  /** The address shoppers are sent to; the store's other addresses redirect to it. */
  isPrimary: z.boolean(),
  /** What is missing (PENDING, VERIFYING, FAILED) or was lost (DNS_LOST); null otherwise. */
  reason: domainReasonSchema.nullable(),
  lastCheckedAt: z.iso.datetime().nullable(),
  /** When the worker looks again; null when no check is planned. */
  nextCheckAt: z.iso.datetime().nullable(),
  /** When the checks of a new domain give up; null once it is active or failed. */
  verifyDeadlineAt: z.iso.datetime().nullable(),
  activatedAt: z.iso.datetime().nullable(),
  /** The end of the certificate the last probe saw; null when no probe ran. */
  tlsValidUntil: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type Domain = z.infer<typeof domainSchema>;

/** A store owner as the platform sees it: identity only, never the store's business data. */
export const tenantOwnerSchema = z.object({
  userId: z.uuid(),
  email: z.string(),
  displayName: z.string(),
  /** True until the owner sets a password through their invitation. */
  needsPasswordSetup: z.boolean(),
  /** When the newest open invitation expires; null when none is open. */
  invitationExpiresAt: z.iso.datetime().nullable(),
});
export type TenantOwner = z.infer<typeof tenantOwnerSchema>;

/**
 * The store's customer number: the card code of the customer of Avrahami's original store the
 * store is (adr/0038). The platform accepts only a customer the original store knows. A catalog
 * product's SKU in the store is `<source SKU>/<customer number>`.
 */
export const customerNumberSchema = z
  .string()
  .trim()
  .regex(CUSTOMER_NUMBER_PATTERN, 'letters, digits, ".", "_" or "-", at most 15');

/** The customer the store is, as the original store last answered for it (adr/0038). */
export const storeCustomerSchema = z.object({
  /** The customer's name in the original store. */
  name: z.string().nullable(),
  /** False when the customer is frozen or not valid there. */
  active: z.boolean(),
  /** The name of the customer's price list there; null when it has none the platform may read. */
  priceList: z.string().nullable(),
  /** The catalog items the customer has a price for, and how many of them are its own. */
  pricedItems: z.number().int().min(0),
  specialPrices: z.number().int().min(0),
  checkedAt: z.iso.datetime(),
});
export type StoreCustomer = z.infer<typeof storeCustomerSchema>;

export const tenantDetailSchema = z.object({
  tenant: tenantSummarySchema,
  /** Null only on a store made before adr/0038 that has not been given one. */
  customerNumber: z.string().nullable(),
  /** Null while there is no customer number. */
  customer: storeCustomerSchema.nullable(),
  stores: z.array(storeSchema),
  domains: z.array(domainSchema),
  owners: z.array(tenantOwnerSchema),
});
export type TenantDetail = z.infer<typeof tenantDetailSchema>;

/** Catalog products one store may have excluded, and one request may name. */
export const MAX_PRODUCT_EXCLUSIONS = 5000;

const masterProductIds = z
  .array(z.uuid())
  .max(MAX_PRODUCT_EXCLUSIONS)
  .refine((ids) => new Set(ids).size === ids.length, 'duplicate product');

export const createTenantRequestSchema = z.object({
  slug: slugSchema,
  displayName: displayNameSchema,
  owner: z.object({
    email: z.email().trim().toLowerCase().max(254),
    displayName: displayNameSchema,
  }),
  /**
   * Master products the new store does not get (adr/0023): they are not copied to it, so no
   * copy is written for them, and the store cannot pull them later.
   */
  excludedProductIds: masterProductIds.optional(),
  /**
   * The store's design template (adr/0034); the platform's default when not given. The values
   * of STORE_TEMPLATES, spelt out: storefront.ts reaches this file through identity.ts, so it
   * cannot be imported here (a test keeps the two lists equal).
   */
  template: z.enum(['PANORAMA', 'ATELIER', 'DEPOT', 'FRESH', 'PLANNER']).optional(),
  /** The customer of the original store the new store is (adr/0038). */
  customerNumber: customerNumberSchema,
});
export type CreateTenantRequest = z.infer<typeof createTenantRequestSchema>;

/** Links the store to another customer of the original store (or again to the same one). */
export const updateCustomerNumberRequestSchema = z.object({
  customerNumber: customerNumberSchema,
});
export type UpdateCustomerNumberRequest = z.infer<typeof updateCustomerNumberRequestSchema>;

export const createTenantResponseSchema = tenantDetailSchema.extend({
  owner: z.object({
    userId: z.uuid(),
    /** False when the owner already had an account (it is reused, never overwritten). */
    created: z.boolean(),
    /** True until the owner has set a password through the invitation. */
    needsPasswordSetup: z.boolean(),
    /** The link token when the owner needs a password; shown once, to be sent by hand. */
    invitation: issuedInvitationSchema.nullable(),
  }),
});
export type CreateTenantResponse = z.infer<typeof createTenantResponseSchema>;

/** A catalog product as the products table of one store names it. */
const storeCatalogProductSchema = z.object({
  /** The master product's id. */
  id: z.uuid(),
  name: z.string(),
  sku: z.string(),
  /** The active categories of the catalog the product is placed in. */
  categoryIds: z.array(z.uuid()),
});

/**
 * A catalog product one store does not show (adr/0047): the platform keeps it out (`PLATFORM`),
 * or the original site does not show it to the store's customer (`SITE`, the default).
 */
export const excludedProductSchema = storeCatalogProductSchema.extend({
  reason: z.enum(['PLATFORM', 'SITE']),
});
export type ExcludedProduct = z.infer<typeof excludedProductSchema>;

export const excludedProductListSchema = z.object({
  /** What the store does not show. */
  items: z.array(excludedProductSchema),
  /** What the store shows by the platform's choice, although the site does not show it. */
  shownByChoice: z.array(storeCatalogProductSchema),
});
export type ExcludedProductList = z.infer<typeof excludedProductListSchema>;

/**
 * Stop showing products in a store (`add`), or show them (`remove`). A choice that matches what
 * the site shows the customer returns the product to that default.
 */
export const updateExcludedProductsRequestSchema = z
  .object({ add: masterProductIds.default([]), remove: masterProductIds.default([]) })
  .refine((change) => change.add.length + change.remove.length > 0, 'nothing to change')
  .refine(
    (change) => !change.add.some((id) => change.remove.includes(id)),
    'a product is both added and removed',
  );
export type UpdateExcludedProductsRequest = z.infer<typeof updateExcludedProductsRequestSchema>;

export const updateExcludedProductsResponseSchema = excludedProductListSchema.extend({
  /** Products the store no longer shows. */
  added: z.number().int().min(0),
  /** Products the store shows again. */
  lifted: z.number().int().min(0),
  /** Products shown that the store did not hold: a pull copies them to it now. */
  addedToStore: z.number().int().min(0),
});
export type UpdateExcludedProductsResponse = z.infer<typeof updateExcludedProductsResponseSchema>;

export const tenantLifecycleRequestSchema = z.object({
  reason: z.string().trim().min(1).max(500).optional(),
});

export const tenantIdParamsSchema = z.object({ tenantId: z.uuid() });
export const tenantOwnerParamsSchema = z.object({ tenantId: z.uuid(), userId: z.uuid() });
export const idParamsSchema = z.object({ id: z.uuid() });

export const updateStoreRequestSchema = z.object({
  name: displayNameSchema,
  /** Optimistic lock: the version the client last read. */
  version: z.number().int().min(1),
});
export type UpdateStoreRequest = z.infer<typeof updateStoreRequestSchema>;

export const tenantListQuerySchema = z.object({
  slug: z.string().trim().toLowerCase().max(40).optional(),
});
